import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './CameraPage.css'

const API = process.env.REACT_APP_API_URL || ""

const FEATURES = [
    {
        tag: 'FLIGHT PLANNING',
        title: 'Plan the perfect shot.',
        desc: 'Set your subject, orbit radius and altitude on the map. The app plans the flight path for cinematic results automatically.',
        key: 'camera_plan',
        stat: { val: '360°', label: 'Orbit Control' },
    },
    {
        tag: 'CAMERA CONTROL',
        title: 'Full camera in your hands.',
        desc: 'Control shutter, ISO, aperture and gimbal angle live from the app. No guesswork, no missed shots.',
        key: 'camera_control',
        stat: { val: '4K', label: 'Max Resolution' },
    },
    {
        tag: 'LIVE PREVIEW',
        title: 'See exactly what you shoot.',
        desc: 'Full-screen live video feed with histogram and focus peaking. Know your shot is sharp before you land.',
        key: 'camera_monitor',
        stat: { val: '60 fps', label: 'Live Feed' },
    },
    {
        tag: 'SHOT LIBRARY',
        title: 'Every shot. Organised.',
        desc: 'Browse, tag and export all footage from your missions. GPS-tagged and sorted by date automatically.',
        key: 'camera_library',
        stat: { val: '100%', label: 'GPS Tagged' },
    },
]

function useCountUp(target, duration, active) {
    const [val, setVal] = useState(0)
    useEffect(() => {
        if (!active) return
        let start = 0
        const step = target / (duration / 16)
        const t = setInterval(() => {
            start += step
            if (start >= target) { setVal(target); clearInterval(t) }
            else setVal(Math.floor(start))
        }, 16)
        return () => clearInterval(t)
    }, [active, target, duration])
    return val
}

function HeroStats() {
    const ref = useRef(null)
    const [on, setOn] = useState(false)
    useEffect(() => {
        const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setOn(true) }, { threshold: 0.5 })
        if (ref.current) o.observe(ref.current)
        return () => o.disconnect()
    }, [])
    const a = useCountUp(4, 1000, on)
    const b = useCountUp(60, 1200, on)
    const c = useCountUp(360, 1400, on)
    return (
        <div ref={ref} className="ch-stats">
            <div className="ch-stat"><span className="ch-stat-val cam-mono">{a}K</span><span className="ch-stat-lbl">Max Resolution</span></div>
            <div className="ch-stat-div" />
            <div className="ch-stat"><span className="ch-stat-val cam-mono">{b} fps</span><span className="ch-stat-lbl">Live Feed</span></div>
            <div className="ch-stat-div" />
            <div className="ch-stat"><span className="ch-stat-val cam-mono">{c}°</span><span className="ch-stat-lbl">Orbit Control</span></div>
        </div>
    )
}

function FeatureCard({ f, index, mediaUrl, isVisible, observerRef }) {
    const cardRef = useRef(null)

    useEffect(() => {
        if (observerRef) observerRef(cardRef.current, index)
    }, [index, observerRef])

    const handleMouseMove = (e) => {
        const card = cardRef.current
        if (!card) return
        const rect = card.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const shimmer = card.querySelector('.cam-img-shimmer')
        if (shimmer) shimmer.style.background = `radial-gradient(circle at ${x}px ${y - rect.height * 0.55}px, rgba(255,255,255,0.13), transparent 60%)`
    }

    const handleMouseLeave = () => {
        const card = cardRef.current
        if (!card) return
        const shimmer = card.querySelector('.cam-img-shimmer')
        if (shimmer) shimmer.style.background = ''
    }

    return (
        <div
            ref={cardRef}
            className={`cam-fc-card ${isVisible ? 'cam-fc-card-in' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <div className="cam-fc-img-wrap">
                {mediaUrl
                    ? <img src={mediaUrl} alt={f.tag} className="cam-fc-img" />
                    : (
                        <div className="cam-fc-img-empty">
                            <span className="cam-mono" style={{ color: '#d97706', fontSize: 11 }}>{f.key}</span>
                            <span style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>Upload via Media Manager</span>
                        </div>
                    )
                }
                <div className="cam-img-shimmer" />
                <div className="cam-fc-stat-chip cam-mono">
                    <span className="cam-fc-stat-val">{f.stat.val}</span>
                    <span className="cam-fc-stat-label">{f.stat.label}</span>
                </div>
            </div>
            <div className="cam-fc-body">
                <span className="cam-fc-tag cam-mono">{f.tag}</span>
                <h3 className="cam-fc-card-title">{f.title}</h3>
                <p className="cam-fc-desc">{f.desc}</p>
                <span className="cam-fc-arrow cam-mono">Explore →</span>
            </div>
        </div>
    )
}

export default function CameraPage() {
    const [media, setMedia] = useState({})
    const [textIn, setTextIn] = useState(false)
    const [visible, setVisible] = useState([])
    const observerMap = useRef({})

    useEffect(() => {
        document.title = "Drone Commander GCS - Camera & Cinematic Drone Control | Aviatricks";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Capture cinematic aerial footage with Drone Commander GCS. Plan orbit missions, control camera ISO/Shutter live, and preview 4K video feeds.");
        }

        fetch(`${API}/api/media/camera`)
            .then(r => r.json())
            .then(d => { setMedia(d) })
            .catch(() => { })
        setTimeout(() => setTextIn(true), 400)
    }, [])

    const attachObserver = (el, i) => {
        if (!el || observerMap.current[i]) return
        const o = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setVisible(v => [...new Set([...v, i])]) },
            { threshold: 0.12 }
        )
        o.observe(el)
        observerMap.current[i] = o
    }

    return (
        <div className="cam-wrap">

            {/* ── HERO ── */}
            <section className="ch-hero">
                {media.camera_hero_bg && (
                    <img src={media.camera_hero_bg} alt="" className="ch-bg" />
                )}
                <div className="ch-overlay" />
                <div className="ch-scanlines" />
                <div className="ch-frame ch-tl" /><div className="ch-frame ch-tr" />
                <div className="ch-frame ch-bl" /><div className="ch-frame ch-br" />

                <div className="ch-topbar cam-mono">
                    <div className="ch-topbar-l"><span className="ch-live" />DRONE COMMANDER GCS — CAMERA</div>
                    <div className="ch-topbar-c">RES: 4K · 60FPS &nbsp;|&nbsp; ISO: 400 &nbsp;|&nbsp; SHUTTER: 1/500</div>
                    <div className="ch-topbar-r">GIMBAL: -30° &nbsp;|&nbsp; CAM: ACTIVE</div>
                </div>
                <div className="ch-botbar cam-mono">
                    <span>ORBIT MISSION &nbsp;·&nbsp; RADIUS: 30M</span>
                    <span>GPS LOCKED &nbsp;·&nbsp; STABILISED &nbsp;·&nbsp; 4K</span>
                </div>

                <div className={`ch-content ${textIn ? 'ch-in' : ''}`}>
                    <div className="ch-eyebrow cam-mono">
                        <span className="ch-badge">CAMERA</span>
                        Aviatricks GCS
                    </div>
                    <h1 className="ch-h1">
                        <span className="ch-line ch-l1">Every frame.</span>
                        <span className="ch-line ch-l2">Perfect.</span>
                    </h1>
                    <p className="ch-p">
                        Plan, fly and capture cinematic aerial footage from one simple app.<br />
                        Built for photographers and videographers.
                    </p>
                    <div className="ch-btns">
                        <a href="#features" className="ch-btn-primary">Explore Features ↓</a>
                        <Link to="/contact/support" className="ch-btn-ghost">Get in Touch</Link>
                    </div>
                    <HeroStats />
                </div>

                <div className="ch-scroll cam-mono">scroll to explore</div>
            </section>

            {/* ── FEATURE CARDS ── */}
            <section className="cam-fc-section" id="features">
                {/* Grid lines — same as eco-bg-grid */}
                <div className="cam-bg-grid" />

                <div className="cam-fc-container">
                    <div className="cam-fc-header">
                        <span className="cam-fc-label cam-mono">CAPABILITIES</span>
                        <h2 className="cam-fc-title">Simple to fly.<br />Beautiful to watch.</h2>
                        <p className="cam-fc-sub">Plan orbits, control the camera live, preview your shots, and organise every frame — all from one app.</p>
                    </div>
                    <div className="cam-fc-grid">
                        {FEATURES.map((f, i) => (
                            <FeatureCard
                                key={f.key}
                                f={f}
                                index={i}
                                mediaUrl={media[f.key]}
                                isVisible={visible.includes(i)}
                                observerRef={attachObserver}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="cam-cta">
                {/* Grid lines — same as eco-bg-grid */}
                <div className="cam-bg-grid" />

                <div className="cam-cta-inner">
                    <span className="cam-mono cam-cta-label">Drone Commander GCS — Camera</span>
                    <h2 className="cam-cta-title">Ready to shoot<br />from the sky?</h2>
                    <p className="cam-cta-tagline">Cinematic aerial, elevated.</p>
                    <p className="cam-cta-p">Get in touch and we'll set you up with Drone Commander GCS for your photography or film work.</p>
                    <Link to="/contact/support" className="ch-btn-primary" style={{ fontSize: 15, padding: '15px 36px' }}>Request a Demo</Link>
                </div>
            </section>

        </div>
    )
}